import { isEmpty } from "radash";
import { useEffect } from "react";

import { viewTypes } from "@evolved/constants";
import { filterData, sortData } from "@evolved/views";

import { AddButton } from "../../components/add-button";
import { CreateAccountModal } from "../../components/modals/create-account-modal";
import { DownloadCSV } from "../../components/view/download-csv/download-csv";
import { Extras } from "./extras";
import { FieldSelect } from "../../components/view/fields-select";
import { Filters } from "../../components/view/filters/filters";
import { OpenImportModal } from "../../components/modals/import-data";
import { SaveView } from "../../components/view/save-view";
import { SearchInput } from "../../components/view/search";
import { Sorters } from "../../components/view/sorters";
import { Table } from "../../components/view/table";
import { ViewActions } from "../../components/view/actions";
import { ViewSelect } from "../../components/view/view-select";
import {
  filterConfigKeys,
  reduceSearchResults,
  useSearch,
} from "../use-search";
import { useAccounts } from "../../data/use-accounts";
import { useContacts } from "../../data/use-contacts";
import { useDefaultView } from "../use-default-view";
import { useFieldConfigs } from "../../components/view/use-field-configs";
import { useModal } from "../../hooks/use-modal";
import { useSearchStore } from "../../stores/search";
import { useTags } from "../../data/use-tags";
import { useUsers } from "../../data/use-users";
import { useViewStore } from "../../stores/view";

const CreateAccountButton = () => {
  const { modal, open } = useModal(CreateAccountModal);

  return (
    <>
      {modal}
      <AddButton id="create-account-button" onClick={open}>
        Account
      </AddButton>
    </>
  );
};

export const AccountsRoute = () => {
  const accounts = useAccounts();
  const contacts = useContacts();
  const tags = useTags();
  const users = useUsers();

  const includeArchived = useViewStore(
    (state) => state[viewTypes.ACCOUNT].includeArchived
  );

  useDefaultView(viewTypes.ACCOUNT);

  const fieldConfigs = useFieldConfigs(viewTypes.ACCOUNT);

  const search = useSearch(
    {
      primary: {
        items: accounts.all({ includeArchived }),
        keys: filterConfigKeys(fieldConfigs),
      },
      secondary: [
        { path: "contacts", items: contacts.all(), keys: ["alias"] },
        { path: "tags", items: tags.all(), keys: ["label"] },
        { path: "users", items: users.all(), keys: ["alias"] },
      ],
    },
    viewTypes.ACCOUNT
  );

  useEffect(() => {
    if (useSearchStore.getState()[viewTypes.ACCOUNT].query) {
      search(useSearchStore.getState()[viewTypes.ACCOUNT].query);
    }
  }, [includeArchived, accounts, contacts, tags, users]);

  const searchResult = useSearchStore(
    (state) => state[viewTypes.ACCOUNT].result
  );

  const filters = useViewStore((state) => state[viewTypes.ACCOUNT].filters);
  const sortOrder = useViewStore((state) => state[viewTypes.ACCOUNT].sortOrder);

  let data;

  if (searchResult) {
    data = reduceSearchResults(searchResult)(accounts);
  } else if (!isEmpty(filters)) {
    data = filterData({
      data: accounts.all({ includeArchived }),
      fieldConfigs,
      filters,
    });
  } else {
    data = accounts.all({ includeArchived });
  }

  if (!isEmpty(sortOrder)) {
    data = sortData({
      data,
      fieldConfigs,
      sortOrder,
    });
  }

  return (
    <div
      className="w-full"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto 1fr",
        height: "100%",
      }}
    >
      <div className="mb-4">
        <div className="mb-4 flex items-center">
          <ViewSelect viewType={viewTypes.ACCOUNT} />
          <FieldSelect viewType={viewTypes.ACCOUNT} />
          <ViewActions viewType={viewTypes.ACCOUNT} />
          <SaveView viewType={viewTypes.ACCOUNT} />
          <div className="ml-auto flex">
            <Extras />
            <OpenImportModal type={viewTypes.ACCOUNT} />
            <div className="ml-2">
              <DownloadCSV data={data} viewType={viewTypes.ACCOUNT} />
            </div>
            <div className="ml-2">
              <CreateAccountButton />
            </div>
          </div>
        </div>
        <div className="flex items-start">
          <div className="flex flex-wrap items-center">
            <div className="mr-4">
              <SearchInput search={search} viewType={viewTypes.ACCOUNT} />
            </div>
            <Filters viewType={viewTypes.ACCOUNT} />
            <Sorters viewType={viewTypes.ACCOUNT} />
          </div>
        </div>
      </div>
      <Table data={data} viewType={viewTypes.ACCOUNT} />
    </div>
  );
};
